import { db, storage } from './firebase';
import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { JOURNEY_STAGES, TRUST_STATS, JourneyStage, JourneyQuestion, JourneyTestimonial, JourneyDeliverable } from './journeyContent';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface JourneySettings {
  heroTitle?: string;
  heroSubtitle?: string;
  heroDescription?: string;
  ctaUrl?: string;
  ctaLabel?: string;
  isPublished: boolean;
  lastPublishedAt?: Date;
  lastPublishedBy?: string;
}

export interface JourneyMedia {
  droneDesign?: string;
  cnc?: string;
  crewRaising?: string;
  model3d?: string;
  model3dEngineering?: string;
}

export interface JourneyTrustStats {
  familiesServed: number;
  yearsInBusiness: number;
  averageRating: number;
  reviewCount: number;
  statesServed: number;
}

export interface JourneyData {
  stages: JourneyStage[];
  trustStats: JourneyTrustStats;
  settings: JourneySettings;
  media?: JourneyMedia;
  updatedAt?: Date;
  updatedBy?: string;
}

// ─── Firestore Refs ─────────────────────────────────────────────────────────

const JOURNEY_DOC_ID = 'journey_content';
const journeyRef = () => doc(db, 'settings', JOURNEY_DOC_ID);

// ─── Defaults ───────────────────────────────────────────────────────────────

const DEFAULT_SETTINGS: JourneySettings = {
  isPublished: false,
  ctaUrl: 'https://www.lctimberframes.com/contact',
  ctaLabel: 'Speak with the Founder',
};

const DEFAULT_MEDIA: JourneyMedia = {
  droneDesign: '/videos/journey-drone-design.mp4',
  cnc: '/videos/journey-cnc.mp4',
  crewRaising: '/videos/journey-crew-raising.mp4',
  model3d: '/models/bent.glb',
  model3dEngineering: '/models/bent.glb',
};

export const getDefaultJourneyData = (): JourneyData => ({
  stages: JOURNEY_STAGES,
  trustStats: TRUST_STATS,
  settings: DEFAULT_SETTINGS,
  media: DEFAULT_MEDIA,
});

// ─── Read ───────────────────────────────────────────────────────────────────

export const getJourneyData = async (): Promise<JourneyData> => {
  try {
    const snapshot = await getDoc(journeyRef());
    if (snapshot.exists()) {
      const data = snapshot.data();
      return {
        stages: data.stages || JOURNEY_STAGES,
        trustStats: data.trustStats || TRUST_STATS,
        settings: { ...DEFAULT_SETTINGS, ...data.settings },
        media: { ...DEFAULT_MEDIA, ...data.media },
        updatedAt: data.updatedAt?.toDate?.() || undefined,
        updatedBy: data.updatedBy,
      };
    }
  } catch (err) {
    console.error('[Journey] Failed to load journey data:', err);
  }
  return getDefaultJourneyData();
};

// ─── Real-time Subscription ─────────────────────────────────────────────────

export const subscribeToJourneyData = (
  callback: (data: JourneyData) => void
): Unsubscribe => {
  return onSnapshot(
    journeyRef(),
    (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        callback({
          stages: data.stages || JOURNEY_STAGES,
          trustStats: data.trustStats || TRUST_STATS,
          settings: { ...DEFAULT_SETTINGS, ...data.settings },
          media: { ...DEFAULT_MEDIA, ...data.media },
          updatedAt: data.updatedAt?.toDate?.() || undefined,
          updatedBy: data.updatedBy,
        });
      } else {
        callback(getDefaultJourneyData());
      }
    },
    (error) => {
      console.error('[Journey] Subscription error:', error);
      callback(getDefaultJourneyData());
    }
  );
};

// ─── Write ──────────────────────────────────────────────────────────────────

export const saveJourneyData = async (
  data: Partial<JourneyData>,
  userId?: string,
  userName?: string
): Promise<void> => {
  const current = await getJourneyData();
  const merged: any = {
    ...current,
    ...data,
    updatedAt: Timestamp.now(),
    updatedBy: userName || userId || 'unknown',
  };
  await setDoc(journeyRef(), merged, { merge: true });
};

export const saveJourneyStage = async (
  stageIndex: number,
  stageData: Partial<JourneyStage>,
  userId?: string,
  userName?: string
): Promise<void> => {
  const current = await getJourneyData();
  const stages = [...current.stages];
  stages[stageIndex] = { ...stages[stageIndex], ...stageData };
  await saveJourneyData({ stages }, userId, userName);
};

export const saveJourneyTrustStats = async (
  stats: Partial<JourneyTrustStats>,
  userId?: string,
  userName?: string
): Promise<void> => {
  const current = await getJourneyData();
  const trustStats = { ...current.trustStats, ...stats };
  await saveJourneyData({ trustStats }, userId, userName);
};

export const saveJourneySettings = async (
  settings: Partial<JourneySettings>,
  userId?: string,
  userName?: string
): Promise<void> => {
  const current = await getJourneyData();
  const merged = { ...current.settings, ...settings };
  await saveJourneyData({ settings: merged }, userId, userName);
};

export const saveJourneyMedia = async (
  media: Partial<JourneyMedia>,
  userId?: string,
  userName?: string
): Promise<void> => {
  const current = await getJourneyData();
  const merged = { ...(current.media || DEFAULT_MEDIA), ...media };
  await saveJourneyData({ media: merged }, userId, userName);
};

export const publishJourney = async (
  userId?: string,
  userName?: string
): Promise<void> => {
  await saveJourneySettings(
    {
      isPublished: true,
      lastPublishedAt: new Date(),
      lastPublishedBy: userName || userId,
    },
    userId,
    userName
  );
};

export const unpublishJourney = async (
  userId?: string,
  userName?: string
): Promise<void> => {
  await saveJourneySettings({ isPublished: false }, userId, userName);
};

// ─── Media Upload ───────────────────────────────────────────────────────────

export const uploadJourneyMedia = async (
  file: File,
  path: string
): Promise<string> => {
  const storageRef = ref(storage, `journey/${path}/${Date.now()}_${file.name}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
};

// ─── Helpers ────────────────────────────────────────────────────────────────

export const createEmptyQuestion = (): JourneyQuestion => ({
  id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  question: '',
  answer: '',
});

export const createEmptyTestimonial = (): JourneyTestimonial => ({
  name: '',
  location: '',
  year: new Date().getFullYear(),
  quote: '',
  projectType: '',
});

export const createEmptyDeliverable = (): JourneyDeliverable => ({
  label: '',
  description: '',
});
